import React from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from 'components/Button';
import InputFormControl from 'components/InputFormControl';
import SelectFormControl from 'components/SelectFormControl';
import Subtitle from 'components/Subtitle';
import {
  invoiceUsageOptions,
  satPaymentMethodOptions,
  stateOptions,
} from './netsuiteInvoiceOptions';
import classes from './RequestInvoiceForm.module.scss';

const RequestInvoiceForm = ({ requestInfo, onSubmitHandler }) => {
  const schema = yup.object().shape({
    customer: yup.object().shape({
      companyName: yup.string().required(),
      email: yup.string().email().required(),
      rfc: yup.string().required(),
    }),
    sale: yup.object().shape({
      invoiceUsage: yup.string().required(),
      satPaymentMethod: yup.string().required(),
      billingAddress: yup.object().shape({
        zipCode: yup.string().required(),
        locality: yup.string().required(),
        state: yup.string().required(),
      }),
    }),
  });

  const { handleSubmit, errors, control } = useForm({
    defaultValues: { ...requestInfo },
    resolver: yupResolver(schema),
  });

  return (
    <>
      <form
        className={classes.requestInvoiceFormContainer}
        onSubmit={handleSubmit(onSubmitHandler)}
      >
        <div className={classes.requestInvoiceFormSection}>
          <Subtitle>General</Subtitle>
          <div className={classes.requestInvoiceFormFieldContainer}>
            <InputFormControl
              id="company-name"
              label="Nombre/Razón Social*"
              name="customer.companyName"
              control={control}
              error={
                errors.customer &&
                errors.customer.companyName &&
                'Razón Social es un campo obligatorio.'
              }
              defaultValue=""
            />
          </div>
          <div className={classes.requestInvoiceFormFieldContainer}>
            <InputFormControl
              id="rfc"
              label="RFC*"
              name="customer.rfc"
              control={control}
              error={
                errors.customer &&
                errors.customer.rfc &&
                'RFC es un campo obligatorio.'
              }
            />
          </div>
          <div className={classes.requestInvoiceFormFieldContainer}>
            <InputFormControl
              id="email"
              label="Correo Electrónico*"
              name="customer.email"
              control={control}
              error={
                errors.customer &&
                errors.customer.email &&
                (errors.customer.email.message ===
                'customer.email must be a valid email'
                  ? 'El correo ingresado no es válido.'
                  : 'Correo Electrónico es un campo obligatorio.')
              }
            />
          </div>
        </div>
        <div className={classes.requestInvoiceFormSection}>
          <Subtitle>Dirección</Subtitle>
          <div className={classes.formFieldContainer}>
            <InputFormControl
              id="street"
              label="Calle"
              name="sale.billingAddress.street"
              control={control}
            />
          </div>
          <div className={classes.requestInvoiceFormFieldContainer}>
            <InputFormControl
              id="street-number"
              label="Número Exterior"
              name="sale.billingAddress.streetNumber"
              control={control}
            />
          </div>
          <div className={classes.requestInvoiceFormFieldContainer}>
            <InputFormControl
              id="interior-number"
              label="Numero Interior"
              name="sale.billingAddress.interiorNumber"
              control={control}
            />
          </div>
          <div className={classes.requestInvoiceFormFieldContainer}>
            <InputFormControl
              id="colonia"
              label="Colonia"
              name="sale.billingAddress.colonia"
              control={control}
            />
          </div>
          <div className={classes.requestInvoiceFormFieldContainer}>
            <InputFormControl
              id="zip-code"
              label="Código Postal*"
              name="sale.billingAddress.zipCode"
              control={control}
              error={
                errors.sale &&
                errors.sale.billingAddress &&
                errors.sale.billingAddress.zipCode &&
                'Código Postal es un campo obligatorio si se desea asignar una dirección.'
              }
            />
          </div>
          <div className={classes.requestInvoiceFormFieldContainer}>
            <InputFormControl
              id="locality"
              label="Localidad*"
              name="sale.billingAddress.locality"
              control={control}
              error={
                errors.sale &&
                errors.sale.billingAddress &&
                errors.sale.billingAddress.locality &&
                'Localidad es un campo obligatorio si se desea asignar una dirección.'
              }
            />
          </div>
          <div className={classes.requestInvoiceFormFieldContainer}>
            <SelectFormControl
              id="state"
              label="Estado*"
              name="sale.billingAddress.state"
              defaultValue="AGS"
              options={stateOptions}
              control={control}
              error={
                errors.sale &&
                errors.sale.billingAddress &&
                errors.sale.billingAddress.state &&
                'Estado es un campo obligatorio si se desea asignar una dirección.'
              }
            />
          </div>
        </div>
        <div className={classes.requestInvoiceFormSection}>
          <Subtitle>Facturación</Subtitle>
          <div className={classes.requestInvoiceFormFieldContainer}>
            <SelectFormControl
              id="invoice-usage"
              label="Uso de Factura*"
              name="sale.invoiceUsage"
              defaultValue="1"
              options={invoiceUsageOptions.filter((option) => !option.disabled)}
              control={control}
              error={
                errors.sale &&
                errors.sale.invoiceUsage &&
                'Uso de Factura es un campo obligatorio.'
              }
            />
          </div>
          <div className={classes.requestInvoiceFormFieldContainer}>
            <SelectFormControl
              id="sat-payment-method"
              label="Método de Pago*"
              name="sale.satPaymentMethod"
              defaultValue="1"
              options={satPaymentMethodOptions.filter(
                (option) => !option.disabled
              )}
              control={control}
              error={
                errors.sale &&
                errors.sale.satPaymentMethod &&
                'Método de Pago es un campo obligatorio.'
              }
            />
          </div>
        </div>
        <div className={classes.formButtonContainer}>
          <Button type="submit" disabled={false}>
            Generar Factura
          </Button>
        </div>
      </form>
    </>
  );
};

RequestInvoiceForm.propTypes = {
  requestInfo: PropTypes.shape({
    sale: PropTypes.shape({
      billingAddress: PropTypes.shape({
        street: PropTypes.string,
        streetNumber: PropTypes.string,
        interiorNumber: PropTypes.string,
        zipCode: PropTypes.string,
        locality: PropTypes.string,
        state: PropTypes.string,
      }),
    }),
  }).isRequired,
  onSubmitHandler: PropTypes.func.isRequired,
};

export default RequestInvoiceForm;
